<template>
  <div v-if="group" class="container ltg-edit">
    <div class="section">
      <div v-if="displayCover" class="tw-mb-16">
        <img class="tw-w-full group-cover" v-lazy="displayCover" />
      </div>
      <!-- Form -->
      <validation-observer @submit.prevent="submit" tag="form" ref="ltg">
        <div class="columns is-4">
          <div class="column">
            <div class="item-block">
              <div class="section-block">
                <span class="title">GROUP NAME</span>
              </div>

              <validation-provider
                rules="required"
                name="name"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                  <b-input v-model="group.name"></b-input>
                </b-field>
              </validation-provider>
            </div>

            <div class="item-block">
              <div class="section-block">
                <span class="title">ABOUT</span>
              </div>

              <validation-provider
                rules="required"
                name="about"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                  <quill-editor
                    class="quill-about"
                    :class="{ 'has-error': errors.length }"
                    v-model="group.about"
                  />
                </b-field>
              </validation-provider>
            </div>

            <div class="item-block">
              <div class="section-block">
                <span class="title">VISION</span>
              </div>

              <validation-provider
                rules="required"
                name="vision"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                  <b-input v-model="group.vision" type="textarea"></b-input>
                </b-field>
              </validation-provider>
            </div>
          </div>
          <div class="column">
            <div class="item-block">
              <div class="section-block">
                <span class="title">GROUP IMAGE</span>
              </div>
              <validation-provider
                rules="required"
                name="cover"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                  <div
                    class="is-block upload-box"
                    :class="{ 'is-danger': errors.length }"
                  >
                    <div class="level">
                      <div class="level-left">
                        <span v-if="displayCover">{{ photoFileName }}</span>
                        <span v-else>No image selected</span>
                      </div>
                      <div class="level-right">
                        <input v-model="displayCover" class="is-hidden" />
                        <b-upload accept="image/*" @input="onPhotoSelect">
                          <a class="button is-primary">
                            <span v-if="displayCover">Change Image</span>
                            <span v-else>Upload Image</span>
                          </a>
                        </b-upload>
                      </div>
                    </div>
                  </div>
                </b-field>
              </validation-provider>
            </div>
            <div class="item-block">
              <div class="section-block">
                <span class="title">GROUP LOCATION</span>
              </div>
              <validation-provider
                rules="required"
                name="address"
                v-slot="{ errors }"
              >
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                  <b-autocomplete
                    field="place_name"
                    v-model="group.address"
                    placeholder="Search address"
                    :data="places"
                    @typing="onSearchAddress"
                  >
                    <span slot="empty">
                      No address found for {{ group.address }}
                    </span>
                  </b-autocomplete>
                </b-field>
              </validation-provider>
            </div>
            <div class="item-block">
              <div class="section-block">
                <span class="title">VALUES</span>
              </div>
              <b-field v-for="(value, key) in values" :key="key">
                <b-input v-model="values[key].values"></b-input>
              </b-field>
              <validation-provider name="values" v-slot="{ errors }">
                <b-field
                  :message="errors"
                  :type="{ 'is-danger': errors.length }"
                >
                </b-field>
              </validation-provider>
            </div>
          </div>
        </div>

        <div>
          <b-button
            :loading="isSubmitting"
            :disabled="isSubmitting"
            native-type="submit"
            type="is-primary"
          >
            Save Changes to Group
          </b-button>
        </div>
      </validation-observer>

      <!-- Show only when there is a group created -->
      <div
        v-infinite-scroll="loadMoreMembers"
        infinite-scroll-distance="10"
        class="columns tw-mt-16"
      >
        <div v-if="group.id" class="column">
          <div class="columns">
            <div class="column is-narrow">
              <div class="section-block">
                <span class="title">GROUP MEMBERS</span>
              </div>
            </div>
            <div class="column">
              <div class="section-block">
                <b-button
                  @click="onboardMember"
                  class="tw-my-4"
                  type="is-primary"
                >
                  Onboard new member
                </b-button>
              </div>
            </div>
          </div>

          <table
            class="table is-fullwidth is-borderless is-family-other is-size-6"
          >
            <thead>
              <tr>
                <th colspan="2">Name</th>
                <th>Email</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="table-item-count" colspan="5">
                  <span>USERS ({{ members.meta.total }})</span>
                </td>
              </tr>
              <member-row
                v-for="member in members.data"
                :key="member.id"
                :member="member"
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <onboard-member @added="onMemberAdded" ref="onboardMember" />
    <!-- Cropper modal -->
    <cropper
      ref="cropper"
      :aspect-ratio="16 / 9"
      :initial-aspect-ratio="16 / 9"
      @change="onPhotoCropped"
    />
  </div>
</template>

<script>
import OnboardMember from '@/components/modals/OnboardMember'
import Cropper from '@/components/modals/Cropper'
import { mapActions, mapState } from 'vuex'
import { map, each, concat, debounce, filter, cloneDeep } from 'lodash'
import InfiniteScroll from 'vue-infinite-scroll'
import User from '@/models/User'
import Group from '@/models/Group'
import QuillEditor from '@/components/QuillEditor'
import UploadMixin from '@/utils/mixins/upload'
import MemberRow from './components/UserRow'

export default {
  mixins: [UploadMixin],
  metaInfo() {
    if (this.group) {
      return {
        title: this.group.name,
        titleTemplate: 'Edit | %s'
      }
    }
  },
  components: {
    OnboardMember,
    Cropper,
    QuillEditor,
    MemberRow
  },
  directives: {
    InfiniteScroll
  },
  data() {
    return {
      isSubmitting: false,
      photoFileName: '',
      coverImage: null,
      selectedCover: null,
      places: [],
      members: {
        data: [],
        meta: {
          current_page: 1
        }
      }
    }
  },
  async mounted() {
    try {
      await this.getCurrentGroup()
      if (this.group.is_owner || this.group.is_group_leader) {
        await this.getGroupMembers()
      } else {
        this.$router.replace({ name: 'groups.details' })
      }
    } catch ({ response }) {
      if (response.status === 404) {
        this.$store.commit('group/setCurrentGroup', new Group({}))
      } else {
        this.$router.replace({ name: 'home' })
      }
    }
  },
  computed: {
    ...mapState({
      group: state => cloneDeep(state.group.currentGroup)
    }),
    displayCover() {
      if (this.coverImage) {
        return this.coverImage
      } else if (this.group && this.group.cover) {
        return this.group.cover.url
      }
      return null
    },
    validValues() {
      if (this.group) {
        return filter(this.values, value => {
          return value.values
        })
      }
      return []
    },
    values: {
      get() {
        let values = this.group ? this.group.values || [] : []
        values.length = 5
        values = map(values, value => {
          return {
            values: '',
            ...value
          }
        })
        return values
      },
      set() {}
    }
  },
  methods: {
    ...mapActions({
      getCurrentGroup: 'group/getCurrentGroup'
    }),
    getGroupMembers() {
      this.group
        .users()
        .params({
          page: this.members.meta.current_page
        })
        .get()
        .then(data => {
          this.members.data = concat(
            this.members.data,
            map(data.data, member => {
              return new User(member)
            })
          )
          this.members.meta = data.meta
        })
    },
    onSearchAddress: debounce(function(address) {
      this.$api
        .get('/group-address', {
          params: {
            address
          }
        })
        .then(({ data }) => {
          this.places = data.data
        })
    }, 500),
    onboardMember() {
      this.$refs.onboardMember.open()
    },
    onMemberAdded() {
      this.members.meta.current_page = 1
      this.members.data = []
      this.getGroupMembers()
    },
    onPhotoSelect(file) {
      if (this.validateFileSize(file)) {
        this.photoFileName = file.name
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = e => {
          this.$refs.cropper.open(e.target.result)
        }
      }
    },
    onPhotoCropped(file) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = e => {
        this.coverImage = reader.result
        this.selectedCover = file
      }
    },
    loadMoreMembers() {
      const { meta } = this.members
      if (meta.current_page < meta.last_page && this.group.id) {
        ++this.members.meta.current_page
        this.getGroupMembers()
      }
    },
    async submit() {
      const isValid = await this.$refs.ltg.validate()
      if (isValid) {
        this.isSubmitting = true
        const { name, about, vision, address } = this.group
        const formData = new FormData()
        if (this.group.id) {
          formData.append('_method', 'PUT')
        }
        each(
          {
            name,
            about,
            vision,
            address,
            cover: this.selectedCover,
            values: JSON.stringify(this.values)
          },
          (value, key) => {
            if (value) {
              formData.append(key, value)
            }
          }
        )

        this.$api
          .post(`/groups/${this.group.id || ''}`, formData)
          .then(() => {
            this.$router.push({ name: 'groups.details' })
          })
          .catch(({ response }) => {
            if (response.data && response.data.errors) {
              this.$refs.ltg.setErrors(response.data.errors)
            }
          })
          .then(() => {
            this.isSubmitting = false
          })
      }
    }
  }
}
</script>
